import { visuallyHidden } from '@insify/css/classes.module.css';
import clsx from 'clsx';

import { textMdSemibold, textXxsSemibold } from '../../../../typography';

import * as styles from './RadioOptionButton.module.css';

import type { InputRadioGroupOptionProps, RadioOption } from '../..';

export type InputKind = 'solid' | 'outline';

export type RadioOptionButtonProps<ValueType> = RadioOption<ValueType> &
  Pick<InputRadioGroupOptionProps<ValueType>, 'label' | 'after' | 'checked' | 'error' | 'hint'> & {
    /* deprecated */
    small?: boolean;
    /* deprecated */
    centered?: boolean;
    input: React.ReactElement;
    className?: string;
    children?: React.ReactNode;
  };

export const BareRadioOptionButton = <ValueType,>({
  input,
  hint,
  checked,
  error,
  disabled = false,
  children,
  className,
}: Omit<RadioOptionButtonProps<ValueType>, 'label' | 'after' | 'small' | 'centered'>) => (
  <div
    className={clsx(
      styles.wrapper,
      error && styles.error,
      hint && styles.withHint,
      checked && styles.checked,
      disabled && styles.disabled,
      className,
    )}
  >
    {hint && <div className={clsx(textXxsSemibold, styles.hint)}>{hint}</div>}
    <span className={visuallyHidden}>{input}</span>
    {children}
  </div>
);

export const RadioOptionButton = <ValueType,>({
  label,
  after,
  small = false,
  centered = false,
  className,
  children,
  ...props
}: RadioOptionButtonProps<ValueType>) => {
  return (
    <BareRadioOptionButton className={clsx(small && styles.small, className)} {...props}>
      <div className={styles.columns}>
        <div className={clsx(textMdSemibold, styles.label, centered && styles.centered)}>{label}</div>
        {after && <div className={styles.after}>{typeof after === 'function' ? after(props.checked) : after}</div>}
      </div>
      {children}
    </BareRadioOptionButton>
  );
};

export const RadioOptionButtonWithInput = <ValueType,>({
  input,
  label,
  ...props
}: RadioOptionButtonProps<ValueType>) => {
  const labelWithInput = (
    <div className={styles.withInput}>
      <div className={styles.resetLineHeight}>{input}</div>
      <div>{label}</div>
    </div>
  );

  return <RadioOptionButton input={<></>} label={labelWithInput} {...props} />;
};
